import { authApi } from 'api';

export const createClubMember = async (userInfo) => {
  return await authApi.post('/Users/add-member', userInfo);
};

/**
 * Updates the details of a specific club member.
 *
 * This function sends a PUT request to the `/users/clubs/{clubId}/members/{email}` endpoint
 * to update the information of a club member identified by their email address within a specific club.
 *
 * @param {string} clubId - The unique identifier of the club where the member belongs.
 * @param {string} email - The email address of the member to be updated.
 * @param {Object} updateInfo - An object containing the updated member information.
 * @returns {Promise<Object>} - A promise that resolves with the server's response,
 *   typically containing the updated member details or confirmation of the update.
 *
 * Example usage:
 * const updatedMember = await updateMember('12345', 'member@example.com', { name: 'John Doe', status: 'active' });
 */
export const updateMember = async (clubId, email, updateInfo) => {
  return await authApi.put(
    `/users/clubs/${clubId}/members/${email}`,
    updateInfo
  );
};

/**
 * Deletes a club member
 * @param {string} clubId - The club ID
 * @param {string} email - The member's email
 */
export const deleteMember = async (clubId, email) => {
  return await authApi.delete(`/users/clubs/${clubId}/members/${email}`);
};

/**
 * Adds a secondary contact to a primary contact's family.
 *
 * This function sends a POST request to create a new secondary contact for a family.
 * All athletes with the same primary contact will get the same secondary contact.
 *
 * @param {string} clubId - The unique identifier of the club.
 * @param {string} primaryContactId - The user ID of the primary contact (member).
 * @param {Object} contactData - The secondary contact information.
 * @param {string} contactData.email - The email address of the secondary contact.
 * @param {string} contactData.firstName - The first name of the secondary contact.
 * @param {string} contactData.lastName - The last name of the secondary contact.
 * @param {boolean} contactData.createUserIfNotExists - Whether to create a new user if they don't exist.
 * @returns {Promise<Object>} - A promise that resolves with the server's response.
 *
 * Example usage:
 * const contactData = {
 *   email: 'secondary@example.com',
 *   firstName: 'Jane',
 *   lastName: 'Smith',
 *   createUserIfNotExists: true
 * };
 * const response = await addSecondaryContact('12345', 'user123', contactData);
 */
export const addSecondaryContact = async (
  clubId,
  primaryContactId,
  contactData
) => {
  return await authApi.post(
    `/Users/clubs/${clubId}/members/${primaryContactId}/secondary-contact`,
    contactData
  );
};

/**
 * Updates an existing secondary contact for a family.
 *
 * This function sends a PUT request to update a secondary contact's information.
 * Permission levels determine which fields can be updated.
 *
 * @param {string} clubId - The unique identifier of the club.
 * @param {string} primaryContactId - The user ID of the primary contact (member).
 * @param {Object} contactData - The updated secondary contact information.
 * @param {string} contactData.userID - The user ID of the secondary contact to be updated.
 * @param {string} contactData.email - The email address of the secondary contact.
 * @param {string} contactData.firstName - The first name of the secondary contact.
 * @param {string} contactData.lastName - The last name of the secondary contact.
 * @param {string} [contactData.role] - The role (Admin/Owner can update this).
 * @param {string} [contactData.participationStatus] - The participation status (Admin/Owner can update this).
 * @returns {Promise<Object>} - A promise that resolves with the server's response.
 *
 * Example usage:
 * const contactData = {
 *   userID: 'secondary123',
 *   email: 'secondary@example.com',
 *   firstName: 'Jane',
 *   lastName: 'Smith-Updated',
 *   role: 'Member',
 *   participationStatus: 'Active'
 * };
 * const response = await updateSecondaryContact('12345', 'user123', contactData);
 */
export const updateSecondaryContact = async (
  clubId,
  primaryContactId,
  contactData
) => {
  return await authApi.put(
    `/Users/clubs/${clubId}/members/${primaryContactId}/secondary-contact`,
    contactData
  );
};

/**
 * Preflight lookup used before add-member / add-guest-member / add-secondary-contact
 * flows. Non-mutating. Returns existence + on-record name, platform-wide contact-role
 * state (sourced from AthleteProfile — applies across all clubs), and this-club
 * membership state. Lets the UI block with a precise reason or confirm with the
 * right copy instead of relying on post-submit error toasts.
 *
 * Admin/Owner only.
 *
 * @param {string} clubId
 * @param {string} email
 * @returns {Promise<{data: {
 *   exists: boolean,
 *   userID?: string,
 *   firstName?: string,
 *   lastName?: string,
 *   email?: string,
 *   emailConfirmed?: boolean,
 *   platformContactRole?: {
 *     isPrimaryContact: boolean,
 *     isSecondaryContact: boolean,
 *     secondaryForFamilyPrimaryUserID?: string, // only when it is THIS family (family-scoped lookup); never another
 *   },
 *   thisClubMembership?: { role: string, participationStatus: string, contactType: string } | null,
 *   platformProtectedRole?: 'PortalAdmin' | 'AccountOwner' | null,
 * }}>}
 */
export const lookupUserByEmail = async (clubId, email) => {
  return await authApi.get(
    `/Users/clubs/${clubId}/lookup-by-email`,
    { params: { email } }
  );
};

/**
 * Duplicate-detection preflight for the add-secondary-contact flow: find OTHER accounts in this club that share an
 * exact first+last name; never anyone outside the club (Doug, 2026-11-05). Emails are masked server-side per the
 * viewer. Authorized: club Admin/Owner, or the family's active primary contact.
 *
 * @param {string} clubId
 * @param {string} primaryContactId - the family being added to (scopes authorization).
 * @param {string} firstName
 * @param {string} lastName
 * @param {string} [excludeUserId] - account to exclude (e.g. the one already selected).
 * @returns {Promise<{data: { matches: Array<{
 *   userID: string, firstName: string, lastName: string, email?: string,
 *   emailConfirmed: boolean, isMemberOfThisClub: boolean, thisClubRole?: string,
 *   isPrimaryContact: boolean, isSecondaryContact: boolean
 * }> }}>}
 */
export const findNameMatches = async (clubId, primaryContactId, firstName, lastName, excludeUserId) => {
  return await authApi.get(
    `/Users/clubs/${clubId}/members/${primaryContactId}/name-matches`,
    { params: { firstName, lastName, excludeUserId } }
  );
};

/**
 * Admin duplicate-detection for the add-member / add-guest flow: club-scoped variant of findNameMatches (no family
 * context). Finds OTHER accounts in this club that share an exact first+last name, so an admin doesn't add a person
 * again under a different email. Never anyone outside the club (Doug, 2026-11-05): someone from elsewhere is found by
 * their email (lookupUserByEmail). Admin/Owner only.
 *
 * @param {string} clubId
 * @param {string} firstName
 * @param {string} lastName
 * @param {string} [excludeUserId]
 * @returns {Promise<{data: { matches: Array<{
 *   userID: string, firstName: string, lastName: string, email?: string,
 *   emailConfirmed: boolean, isMemberOfThisClub: boolean, thisClubRole?: string,
 *   isPrimaryContact: boolean, isSecondaryContact: boolean
 * }> }}>}
 */
export const findClubNameMatches = async (clubId, firstName, lastName, excludeUserId) => {
  return await authApi.get(
    `/Users/clubs/${clubId}/name-matches`,
    { params: { firstName, lastName, excludeUserId } }
  );
};

/**
 * Lean club directory (members + guests) for the add-secondary search picker. Emails are masked server-side
 * for non-admin viewers. Authorized: club Admin/Owner, or the family's active primary contact.
 *
 * @param {string} clubId
 * @param {string} primaryContactId - the family being added to (scopes authorization; excluded from results).
 * @returns {Promise<{data: Array<{
 *   userID: string, firstName: string, lastName: string, email?: string, role: string,
 *   invitationStatus: string, participationStatus: string, contactRole: string, linkedPrimaryName?: string
 * }>}>}
 */
export const getSecondaryCandidates = async (clubId, primaryContactId) => {
  return await authApi.get(
    `/Users/clubs/${clubId}/members/${primaryContactId}/secondary-candidates`
  );
};

/**
 * Family-scoped existing-account preflight for the add-secondary-contact flow: reports whether an account with
 * this email already exists (and who/what they are) so the UI can confirm "add this existing person?" or block,
 * rather than silently creating a duplicate. Authorized: club Admin/Owner, or the family's active primary contact.
 *
 * @param {string} clubId
 * @param {string} primaryContactId
 * @param {string} email
 * @returns {Promise<{data: { exists: boolean, userID?: string, firstName?: string, lastName?: string,
 *   email?: string, emailConfirmed?: boolean, platformContactRole?: object, thisClubMembership?: object,
 *   platformProtectedRole?: string }}>}
 */
export const lookupSecondaryCandidateByEmail = async (clubId, primaryContactId, email) => {
  return await authApi.get(
    `/Users/clubs/${clubId}/members/${primaryContactId}/lookup-by-email`,
    { params: { email } }
  );
};

/**
 * Unlinks the family's secondary contact in a club. Severs SecondaryContactUserID
 * on the family's athletes in this club; the former secondary contact KEEPS their
 * club membership (relabelled NoAthletes) — they are never auto-removed. Idempotent.
 *
 * Authorized: club Admin/Owner, OR the family's primary contact (any active level,
 * including Guest). See docs/role-and-deletion-design.html §5.3.
 *
 * @param {string} clubId - The unique identifier of the club.
 * @param {string} primaryContactId - The user ID of the family's primary contact.
 * @returns {Promise<Object>}
 */
export const unlinkSecondaryContact = async (clubId, primaryContactId) => {
  return await authApi.delete(
    `/Users/clubs/${clubId}/members/${primaryContactId}/secondary-contact`
  );
};

/**
 * Fetch the list of members for a specific club by sending a GET request to the '/users/{clubId}/members' endpoint.
 *
 * @param {string} clubId - The ID of the club whose members are to be fetched.
 * @returns {Promise} - The response from the API containing the list of members.
 */
export const getMembers = async (clubId) => {
  return await authApi.get(`/users/${clubId}/members-profiles`);
};

/**
 * Fetches a club member's details by their email.
 *
 * This function sends a GET request to the `/users/{clubId}/members/{email}` endpoint
 * to retrieve details of a specific club member associated with the given club ID and email.
 *
 * @param {string} clubId - The unique identifier of the club.
 * @param {string} email - The email address of the member to fetch.
 * @returns {Promise<Object>} - A promise that resolves with the response from the server,
 *   containing the club member's details.
 *
 * Example usage:
 * const memberDetails = await getClubMemberByEmail('12345', 'member@example.com');
 */
export const getClubMemberByEmail = async (clubId, email) => {
  return await authApi.get(`/users/${clubId}/members/${email}`);
};

/**
 * Fetches athletes associated with a specific club member's email.
 *
 * This function sends a GET request to the `/users/{clubId}/members/{email}/athletes` endpoint
 * to retrieve the list of athletes linked to the specified club member identified by their email.
 *
 * @param {string} clubId - The unique identifier of the club.
 * @param {string} email - The email address of the member whose athletes are to be fetched.
 * @returns {Promise<Array>} - A promise that resolves with the response from the server,
 *   containing an array of athletes linked to the club member.
 *
 * Example usage:
 * const athletes = await getAthletesByMemberEmail('12345', 'member@example.com');
 */
export const getAthletesByMemberEmail = async (clubId, email) => {
  return await authApi.get(`/users/clubs/${clubId}/members/${email}/athletes`);
};

// ── Personal portal (club-independent) ───────────────────────────────────────
// The logged-in user's own profile + athletes, sourced from User / AthleteProfile
// directly so they work for a citizen with zero active clubs (e.g. an expired visitor).
export const getMyProfile = async () => {
  return await authApi.get('/users/me');
};

export const updateMyProfile = async (payload) => {
  return await authApi.put('/users/me', payload);
};

export const getMyAthletes = async () => {
  return await authApi.get('/users/me/athletes');
};

// The whole personal dashboard (profile + cross-club contacts + athletes with nested meets).
export const getMyDashboard = async () => {
  return await authApi.get('/users/me/dashboard');
};

// --- Personal-portal family edits (caller is the family's primary contact) ---

// Create a no-club athlete profile in the caller's family (identity only).
export const createMyAthlete = async (payload) => {
  return await authApi.post('/users/me/athletes', payload);
};

// Edit one of the caller's athletes' identity (name / gender / DOB).
export const updateMyAthlete = async (athleteId, payload) => {
  return await authApi.put(`/users/me/athletes/${athleteId}`, payload);
};

// Remove one of the caller's athletes from a club where they are a guest/visitor.
export const removeMyAthleteFromClub = async (athleteId, clubId) => {
  return await authApi.delete(`/users/me/athletes/${athleteId}/clubs/${clubId}`);
};

// Delete a clubless athlete profile from the caller's family.
export const deleteMyAthlete = async (athleteId) => {
  return await authApi.delete(`/users/me/athletes/${athleteId}`);
};

// Remove the family's secondary contact (they keep any club memberships).
export const removeMySecondaryContact = async () => {
  return await authApi.delete('/users/me/secondary-contact');
};

/**
 * Adds a new athlete to the system using the primary cnotact member email.
 *
 * This function sends a POST request to the `/users/add-athlete-by-email` endpoint
 * with the athlete's information to register them in the system.
 *
 * @param {Object} athleteInfo - An object containing the athlete's details, such as name, email, and other relevant data.
 * @returns {Promise<Object>} - A promise that resolves with the server's response,
 *   typically confirming the successful addition of the athlete or providing details about the newly added athlete.
 *
 * Example usage:
 * const newAthlete = await addAthlete({ name: 'Jane Doe', email: 'jane.doe@example.com', clubId: '12345' });
 */
export const addAthlete = async (athleteInfo) => {
  return await authApi.post('/users/add-athlete-by-email', athleteInfo);
};

/**
 * Sends an invitation to join a club.
 *
 * This function sends a POST request to the `/users/send-club-invitation` endpoint
 * to invite a user to join a specific club. It includes the option to force resending
 * the invitation if it has already been sent previously.
 *
 * @param {string} clubID - The unique identifier of the club to which the invitation is being sent.
 * @param {string} email - The email address of the user to whom the invitation is sent.
 * @returns {Promise<Object>} - A promise that resolves with the server's response, typically confirming the invitation's success or providing error details.
 *
 * Example usage:
 * const response = await sendInvitation('12345', 'user@example.com');
 * console.log(response);
 */
export const sendInvitation = async (clubID, email) => {
  return await authApi.post(`/users/send-club-invitation`, {
    clubID,
    email,
    forceResend: true,
  });
};

/**
 * Sends invitation emails to every not-yet-invited active contact in a roster scope, in one batch.
 *
 * Backend re-derives the target list at call time (NotInvited + Active), so re-running only retries
 * prior failures. Response: { attemptedCount, sentCount, failed: [{ email, name, reason }] }.
 *
 * @param {string} clubID - The club whose roster is being invited.
 * @param {string} scope - 'members' | 'guests' | 'visitors' (which roster view's contacts to target).
 * @param {string} [meetID] - Optional meet (must belong to the club); names the meet in visitor emails.
 * @returns {Promise<Object>} - Axios response whose data carries the per-recipient outcome.
 */
export const sendClubInvitationsBulk = async (clubID, scope, meetID) => {
  return await authApi.post(`/users/send-club-invitations-bulk`, {
    clubID,
    scope,
    meetID: meetID || null,
  });
};

/**
 * Fetches the details of an athlete by their profile ID within a specific club.
 *
 * This function sends a GET request to the `/users/clubs/:clubId/athlete/:athleteProfileId` endpoint
 * to retrieve information about a specific athlete in a given club.
 *
 * @param {string} clubId - The unique identifier of the club to which the athlete belongs.
 * @param {string} athleteProfileId - The unique identifier of the athlete's profile.
 * @returns {Promise<Object>} - A promise that resolves with the athlete's details from the server.
 *
 * Example usage:
 * const athleteDetails = await getAthlete('12345', '67890');
 * console.log(athleteDetails);
 */
export const getAthlete = async (clubId, athleteProfileId) => {
  return await authApi.get(
    `/users/clubs/${clubId}/athlete/${athleteProfileId}`
  );
};

/**
 * Updates the details of an athlete in a specific club.
 *
 * This function sends a PUT request to the `/users/clubs/:clubId/athletes/:athleteId` endpoint
 * with the updated athlete information to modify the athlete's details.
 *
 * @param {string} clubId - The unique identifier of the club the athlete belongs to.
 * @param {string} athleteId - The unique identifier of the athlete to be updated.
 * @param {Object} updateInfo - An object containing the updated athlete details.
 *   This could include properties like `name`, `age`, `team`, or other attributes.
 * @returns {Promise<Object>} - A promise that resolves with the server's response,
 *   indicating the success or failure of the update.
 *
 * Example usage:
 * const updateInfo = { name: 'John Doe', team: 'Team A' };
 * const response = await updateAthlete('12345', '67890', updateInfo);
 * console.log(response);
 */
export const updateAthlete = async (clubId, athleteId, updateInfo) => {
  return await authApi.put(
    `/users/clubs/${clubId}/athletes/${athleteId}`,
    updateInfo
  );
};

/**
 * Warn-on-duplicate check for the add/edit-athlete flow and the Meet Registrations popup: given an athlete's identity
 * (name + gender, DOB optional), find existing AthleteProfiles that may be the SAME athlete, so an admin doesn't create
 * a duplicate. Record-linkage: exact DOB+gender (with fuzzy name) + exact-name, tiered by confidence (Exact /
 * LikelyFuzzyName / SameNameVerify). Official/Admin of the club.
 *
 * Privacy (Doug, 2026-11-05): an athlete outside this club comes back only when contactEmail is that athlete's primary
 * contact email (its own family); never another club's athlete. No match carries its other clubs (clubs is empty).
 * requireSharedDetail (the athlete form): this club's athletes only when they share the birth date or the family.
 *
 * @param {string} clubId
 * @param {{ firstName: string, lastName: string, gender: string, birthDate?: string, contactEmail?: string,
 *           requireSharedDetail?: boolean, excludeAthleteId?: string, includeContactEmail?: boolean }} params
 * @returns {Promise<{data: Array<{ athleteProfileID: string, firstName: string, lastName: string, birthDate: string,
 *   gender: string, primaryContactUserID: string, primaryContactName: string, isSameFamily: boolean,
 *   isInThisClub: boolean, matchTier: 'Exact' | 'LikelyFuzzyName' | 'LikelyDobTypo' | 'SameNameVerify' }>}>}
 */
export const findAthleteMatches = async (clubId, params) => {
  return await authApi.get(`/Users/clubs/${clubId}/athlete-matches`, { params });
};

/**
 * Deletes an athlete from a specific club.
 *
 * @param {string} clubId - The unique identifier of the club.
 * @param {string} athleteId - The unique identifier of the athlete to be deleted.
 * @returns {Promise<Object>} - A promise that resolves with the server's response.
 *
 * Example usage:
 * await deleteAthlete('12345', '67890');
 */
export const deleteAthlete = async (clubId, athleteId) => {
  return await authApi.delete(`/Users/clubs/${clubId}/athletes/${athleteId}`);
};

/**
 * Links an existing platform AthleteProfile to a club by creating an AthleteClubMembership.
 * The profile itself is unchanged (name, birthDate, gender, primary contact) — only the
 * per-club fields in `linkData` are set.
 *
 * @param {string} clubId
 * @param {string} athleteId - AthleteProfileID of the existing profile
 * @param {{
 *   membershipType: 'Full' | 'Guest',
 *   participationStatus: 'YES' | 'NO',
 *   clubAthleteNumber: number,
 *   clubRFIDNumber?: string,
 *   expiresAt?: string,           // ISO date — required for Guest
 *   externalClubName?: string,    // optional for Guest
 * }} linkData
 */
export const linkAthleteToClub = async (clubId, athleteId, linkData) => {
  return await authApi.post(
    `/Users/clubs/${clubId}/athletes/${athleteId}/link`,
    linkData
  );
};

/**
 * Suggests a ClubAthleteNumber for a rejoining athlete based on the number
 * they most recently raced under in this club.
 *
 * @param {string} clubId
 * @param {string} athleteId - AthleteProfileID
 * @returns {Promise<{data: { suggestedNumber: number | null, reason: 'previous' | 'previous-number-in-use' | 'no-history' }}>}
 */
export const getSuggestedClubAthleteNumber = async (clubId, athleteId) => {
  return await authApi.get(
    `/Users/clubs/${clubId}/athletes/${athleteId}/suggested-club-number`
  );
};

// -----------------------------------------------------------------------------
// Bulk club-scoped operations. Each returns BulkOperationResultDto:
//   { succeededCount, skippedCount, results: [{ id, success, reason }] }
// Per the role-and-deletion-design §5.5: every per-item operation is validated
// against the same guards as the single-item version and is skipped-and-reported
// on failure, never forced.
// -----------------------------------------------------------------------------

export const bulkRemoveAthletes = async (clubId, athleteIds) => {
  return await authApi.post(`/Users/clubs/${clubId}/athletes/bulk-remove`, {
    athleteIds,
  });
};

/**
 * Bulk-set participation status for FULL athletes — the bulk counterpart of the
 * family-profile participation edit. Guest-class athletes are skipped-and-reported
 * (their status is derived from their expiry window, not admin-set).
 *
 * @param {string} clubId
 * @param {string[]} athleteIds
 * @param {'YES' | 'NO'} participationStatus
 */
export const bulkSetAthleteParticipation = async (clubId, athleteIds, participationStatus) => {
  return await authApi.post(`/Users/clubs/${clubId}/athletes/bulk-set-participation`, {
    athleteIds,
    participationStatus,
  });
};

/**
 * Bulk-set the team (school house, faction) of FULL athletes: puts every
 * listed athlete in one of the club's teams, or in none. One read and one
 * update on the server whatever the number of athletes. Guest-class athletes
 * are skipped-and-reported.
 *
 * @param {string} clubId
 * @param {string[]} athleteIds
 * @param {string} teamID - the id of one of the club's teams, or '' for no team
 */
export const bulkSetAthleteTeam = async (clubId, athleteIds, teamID) => {
  return await authApi.post(`/Users/clubs/${clubId}/athletes/bulk-set-team`, {
    athleteIds,
    teamID: teamID || '',
  });
};

export const bulkRemoveMembers = async (clubId, memberIds) => {
  return await authApi.post(`/Users/clubs/${clubId}/members/bulk-remove`, {
    memberIds,
  });
};

/**
 * Bulk-set participation status for MEMBERS (§5.7 suspend / restore).
 * 'Active' restores the membership only (athletes are reactivated athlete-first).
 * 'Inactive' SUSPENDS: the family's athletes are cascaded inactive, their upcoming
 * meet registrations are dropped, and the family's other contacts are derived.
 * Self, the Owner, and guest-class members are skipped-and-reported.
 *
 * @param {string} clubId
 * @param {string[]} memberIds
 * @param {'Active' | 'Inactive'} participationStatus
 */
export const bulkSetMemberParticipation = async (clubId, memberIds, participationStatus) => {
  return await authApi.post(`/Users/clubs/${clubId}/members/bulk-set-participation`, {
    memberIds,
    participationStatus,
  });
};

/**
 * Read-only dry-run of a member suspension (§5.7) for the confirm dialog: which members
 * change vs are skipped (with reasons), which athletes will be set inactive, which upcoming
 * meet registrations will be removed, and which family contacts will be stood down.
 *
 * @param {string} clubId
 * @param {string[]} memberIds
 */
export const planSuspendMembers = async (clubId, memberIds) => {
  return await authApi.post(`/Users/clubs/${clubId}/members/suspend-plan`, {
    memberIds,
  });
};

/**
 * Bulk-expire guest athletes. The client passes its local "today" date as
 * yyyy-mm-dd so the server stores a calendar date that matches what the admin
 * sees — using server time would surface the wrong day for admins east/west
 * of the server timezone.
 *
 * @param {string} clubId
 * @param {string[]} athleteIds
 * @param {string} [expiresAt] yyyy-mm-dd. Defaults to today in the caller's local timezone.
 */
export const bulkExpireGuestAthletes = async (clubId, athleteIds, expiresAt) => {
  const dateStr = expiresAt || localTodayIsoDate();
  return await authApi.post(`/Users/clubs/${clubId}/athletes/bulk-expire`, {
    athleteIds,
    expiresAt: dateStr,
  });
};

// yyyy-mm-dd in the browser's local timezone. Built from local Date components
// rather than toISOString() (which serializes UTC and would be off-by-one near
// midnight for admins in negative-offset timezones).
const localTodayIsoDate = () => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

/**
 * @param {string} clubId
 * @param {string[]} athleteIds
 * @param {string} [expiresAt] ISO date applied to all reactivated athletes; omit for backend default.
 */
export const bulkReactivateGuestAthletes = async (clubId, athleteIds, expiresAt) => {
  return await authApi.post(`/Users/clubs/${clubId}/athletes/bulk-reactivate`, {
    athleteIds,
    ...(expiresAt ? { expiresAt } : {}),
  });
};

/**
 * Fetches the most recent result details for a specific athlete in a given club.
 *
 * @param {string} clubId - The ID of the club.
 * @param {string} athleteId - The ID of the athlete.
 * @returns {Promise<Object>} - A promise that resolves with the recent result details.
 */
export const getRecentMeetByAthleteId = async (clubId, athleteId) => {
  return await authApi.post('/athletedashboard/recent-meet', {
    clubId,
    athleteId,
  });
};

/**
 * Fetches the list of past meets for the current season for a specific athlete in a given club.
 *
 * @param {string} clubId - The ID of the club.
 * @param {string} athleteId - The ID of the athlete.
 * @returns {Promise<Object>} - A promise that resolves with the list of past meets for the current season.
 */
export const getPastMeetAtCurrentSeason = async (clubId, athleteId) => {
  return await authApi.post('/athletedashboard/past-meets', {
    clubId,
    athleteId,
  });
};

// seasonId is optional: null = the club's current season. Only the current or the
// immediately previous season is accepted by the server.
export const getAthleteDetailsByAthleteId = async (
  clubId,
  athleteId,
  seasonId = null
) => {
  return await authApi.post('/athletedashboard/athlete-dashboard-results', {
    clubId,
    athleteId,
    seasonId,
  });
};

/**
 * Fetches the event history for a specific athlete in a given club based on the event type.
 *
 * @param {string} clubId - The ID of the club.
 * @param {string} athleteId - The ID of the athlete.
 * @param {string} eventType - The type of the event (e.g., race, competition).
 * @param {string|null} seasonId - Optional season ID (null = current season).
 * @returns {Promise<Object>} - A promise that resolves with the event history.
 */
export const getEventHistory = async (
  clubId,
  athleteId,
  eventType,
  seasonId = null
) => {
  return await authApi.post('/athletedashboard/event-history', {
    clubId,
    athleteId,
    eventType,
    seasonId,
  });
};

// Exports the athlete's season bests for all events as CSV (current season by
// default; pass seasonId for the previous season).
export const exportAthletePbReportToFile = async (
  clubId,
  athleteId,
  seasonId = null
) => {
  return await authApi.post(
    '/athletedashboard/pb-report/export-csv',
    {
      clubId,
      athleteId,
      seasonId,
    },
    {
      responseType: 'blob',
    }
  );
};

export const getMemberDetailByAthleteID = async (clubId, athleteId) => {
  return await authApi.get(
    `/Users/clubs/${clubId}/athletes/${athleteId}/member`
  );
};

/**
 * Fetches complete member details including secondary contact and athletes in single API call.
 *
 * @param {string} clubId - The ID of the club
 * @param {string} email - The email of the member
 * @returns {Promise<Object>} - Complete member details
 */
export const getMemberDetails = async (clubId, email) => {
  return await authApi.get(
    `/Users/clubs/${clubId}/members/${email}/member-details`
  );
};

/**
 * Fetches the list of athletes for a specific club meet.
 *
 * @param {string} clubId - The club ID
 * @returns {Promise} - The response from the API
 */
export const getClubMeetAthletes = async (clubId) => {
  return await authApi.get(`/Users/meet-athletes/${clubId}`);
};

/**
 * Fetches the stats of the athletes for a specific single club meet.
 *
 * @param {string} meetId - The meet ID
 * @returns
 */
export const getMeetAthletesStats = async (meetId) => {
  return await authApi.get(`/Users/meet-athletes-stats/${meetId}`);
};

/**
 * Exports club members to a CSV file.
 *
 * @param {string} clubId - The unique identifier of the club.
 * @param {string} membershipType - The membership type filter ('ClubMembersOnly' or 'GuestsOnly').
 * @param {string[]} [memberIds] - User IDs to limit the file to. Omit to export every member of that type.
 * @returns {Promise<Object>} - A promise that resolves with the CSV file blob.
 */
export const exportClubMembersToFile = async (clubId, membershipType, memberIds) => {
  return await authApi.post(
    `/Clubs/${clubId}/export-members-csv?membershipType=${membershipType}`,
    memberIds ? { memberIds } : null,
    { responseType: 'blob' }
  );
};

/**
 * Exports club athletes to a CSV file.
 *
 * @param {string} clubId - The unique identifier of the club.
 * @param {string[]} [athleteIds] - Athlete IDs to limit the file to. Omit to export every club athlete.
 * @returns {Promise<Object>} - A promise that resolves with the CSV file blob.
 */
export const exportClubAthletesToFile = async (clubId, athleteIds) => {
  return await authApi.post(
    `/Clubs/${clubId}/export-club-athletes-csv`,
    athleteIds ? { athleteIds } : null,
    { responseType: 'blob' }
  );
};
